import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { mealPlanService } from 'api/services/mealPlanService';
import { propertyService } from 'api/services/propertyService';
import type { MealPlan, CreateMealPlanRequest, UpdateMealPlanRequest, MajorMealRule } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import { MAIN_MEAL_OPTIONS, fromMainMeal, toMainMeal, type MainMeal } from 'utils/mealPlanMeals';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface MealPlanModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: MealPlan | null;
  onSave: () => void;
  onClose: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, planCode?: string) => void;
}

interface MealPlanFormData {
  planCode: string;
  planName: string;
  description: string;
  propertyId: string;
  includesBreakfast: boolean;
  /** Lunch and dinner as one choice; saved as includes_lunch / includes_dinner / default_major_meal. */
  mainMeal: MainMeal;
  /** Not on the form (nothing reads it); carried so a save never changes it. */
  includesBeverages: boolean;
  defaultMajorMeal: string;
  majorMealRule: MajorMealRule;
  adultRate: string;
  childRate: string;
  includeTaxes: boolean;
  isActive: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: MealPlanFormData = {
  planCode: '',
  planName: '',
  description: '',
  propertyId: '',
  includesBreakfast: false,
  mainMeal: 'NONE',
  includesBeverages: false,
  defaultMajorMeal: '',
  majorMealRule: 'GUEST_CHOICE',
  adultRate: '',
  childRate: '',
  includeTaxes: false,
  isActive: true,
};

// ============================================================================
// COMPONENT
// ============================================================================

const MealPlanModal: React.FC<MealPlanModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<MealPlanFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Property options for dropdown ---
  const [propertyOptions, setPropertyOptions] = useState<SelectOption[]>([]);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<MealPlan | null | undefined>(record);

  // --- Load property options ---
  useEffect(() => {
    if (!isOpen) return;
    const loadProperties = async () => {
      try {
        const res = await propertyService.getAll({ pageSize: 500, includeInactive: false });
        setPropertyOptions(
          res.data.map(p => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }))
        );
      } catch { /* ignore */ }
    };
    loadProperties();
  }, [isOpen]);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    let data: MealPlanFormData;
    if ((mode === 'edit' || mode === 'copy') && rec) {
      data = {
        planCode: mode === 'copy' ? '' : (rec.planCode || ''),
        planName: rec.planName || '',
        description: rec.description || '',
        propertyId: rec.propertyId || '',
        includesBreakfast: rec.includesBreakfast ?? false,
        mainMeal: toMainMeal(rec),
        includesBeverages: rec.includesBeverages ?? false,
        defaultMajorMeal: rec.defaultMajorMeal ?? '',
        majorMealRule: rec.majorMealRule ?? 'GUEST_CHOICE',
        adultRate: rec.adultRate != null ? String(rec.adultRate) : '',
        childRate: rec.childRate != null ? String(rec.childRate) : '',
        includeTaxes: rec.includeTaxes ?? false,
        isActive: rec.isActive,
      };
    } else {
      data = { ...INITIAL_FORM_DATA };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.planCode?.trim();
      const name = currentFormData.planName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add Meal Plan (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.planCode?.trim();
      const name = currentFormData.planName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.planCode || 'Meal Plan'}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? currentFormData.planCode : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.propertyId) {
      errors.propertyId = 'Property is required';
    }

    if (!formData.planCode.trim()) {
      errors.planCode = 'Plan Code is required';
    } else if (formData.planCode.length > 10) {
      errors.planCode = 'Plan Code cannot exceed 10 characters';
    }

    if (!formData.planName.trim()) {
      errors.planName = 'Plan Name is required';
    } else if (formData.planName.length > 50) {
      errors.planName = 'Plan Name cannot exceed 50 characters';
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description cannot exceed 500 characters';
    }

    if (formData.mainMeal === 'ONE_OF' && !formData.defaultMajorMeal) {
      errors.defaultMajorMeal = 'Default Main Meal is required when guests take one of lunch or dinner';
    }

    if (formData.adultRate) {
      const val = parseFloat(formData.adultRate);
      if (isNaN(val) || val < 0) {
        errors.adultRate = 'Default Adult Rate must be a positive number';
      }
    }

    if (formData.childRate) {
      const val = parseFloat(formData.childRate);
      if (isNaN(val) || val < 0) {
        errors.childRate = 'Default Child Rate must be a positive number';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    // Async code validation for create/copy modes before showing save confirm
    if (mode !== 'edit') {
      try {
        const propertyId = formData.propertyId || undefined;
        const response = await mealPlanService.validateCode(formData.planCode.trim(), propertyId);
        if (!response.data) {
          setFieldErrors({ planCode: 'This plan code already exists' });
          requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>('[data-field="planCode"]');
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector<HTMLElement>('input')?.focus(); }
          });
          return;
        }
      } catch { }
    }

    // Async name uniqueness validation
    {
      try {
        const propertyId = formData.propertyId || undefined;
        const excludeId = mode === 'edit' && record ? record.id : undefined;
        const response = await mealPlanService.validateName(formData.planName.trim(), propertyId, excludeId);
        if (!response.data) {
          setFieldErrors({ planName: 'This plan name already exists' });
          requestAnimationFrame(() => {
            const el = document.querySelector<HTMLElement>('[data-field="planName"]');
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector<HTMLElement>('input')?.focus(); }
          });
          return;
        }
      } catch { }
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record]);

  // --- Confirmed save (3-stage: confirm → loading → toast) ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreateMealPlanRequest | UpdateMealPlanRequest = {
        propertyId: formData.propertyId || undefined,
        planCode: formData.planCode.trim(),
        planName: formData.planName.trim(),
        description: formData.description || undefined,
        includesBreakfast: formData.includesBreakfast,
        includesBeverages: formData.includesBeverages,
        // Lunch, dinner and the default meal come from the Main Meal choice; the default
        // is sent only for "one of lunch or dinner", so a value left over from switching
        // to another choice is never saved.
        ...fromMainMeal(formData.mainMeal, formData.defaultMajorMeal),
        majorMealRule: formData.mainMeal === 'ONE_OF' ? formData.majorMealRule : 'GUEST_CHOICE',
        adultRate: formData.adultRate ? parseFloat(formData.adultRate) : undefined,
        childRate: formData.childRate ? parseFloat(formData.childRate) : undefined,
        includeTaxes: formData.includeTaxes,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await mealPlanService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateMealPlanRequest);
      } else {
        await mealPlanService.create(payload as CreateMealPlanRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.planName}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh record from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await mealPlanService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data: MealPlanFormData = {
          planCode: rec.planCode || '',
          planName: rec.planName || '',
          description: rec.description || '',
          propertyId: rec.propertyId || '',
          includesBreakfast: rec.includesBreakfast ?? false,
          mainMeal: toMainMeal(rec),
          includesBeverages: rec.includesBeverages ?? false,
          defaultMajorMeal: rec.defaultMajorMeal ?? '',
          majorMealRule: rec.majorMealRule ?? 'GUEST_CHOICE',
          adultRate: rec.adultRate != null ? String(rec.adultRate) : '',
          childRate: rec.childRate != null ? String(rec.childRate) : '',
          includeTaxes: rec.includeTaxes ?? false,
          isActive: rec.isActive,
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // --- Code validation on blur ---
  const handleCodeBlur = useCallback(async () => {
    const code = formData.planCode.trim();
    if (!code) return;
    try {
      const propertyId = formData.propertyId || undefined;
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await mealPlanService.validateCode(code, propertyId, excludeId);
      if (!response.data) {
        setFieldErrors(prev => ({ ...prev, planCode: 'This plan code already exists' }));
      }
    } catch {
      // Silently ignore validation errors
    }
  }, [formData.planCode, formData.propertyId, mode, record]);

  // --- Name validation on blur ---
  const handleNameBlur = useCallback(async () => {
    const name = formData.planName.trim();
    if (!name) return;
    try {
      const propertyId = formData.propertyId || undefined;
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await mealPlanService.validateName(name, propertyId, excludeId);
      if (!response.data) {
        setFieldErrors(prev => ({ ...prev, planName: 'This plan name already exists' }));
      }
    } catch {
      // Silently ignore validation errors
    }
  }, [formData.planName, formData.propertyId, mode, record]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {/* Loading overlay */}
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem'
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'edit' ? 'Saving...' : 'Creating...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Meal Plan' : mode === 'copy' ? 'Copy Meal Plan' : 'Edit Meal Plan'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Plan Code & Name --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="planCode">
              <label className="form-label">Plan Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('planCode') ? ' form-field-error' : ''}`}
                value={formData.planCode}
                onChange={(e) => { clearFieldError('planCode'); setFormData(prev => ({ ...prev, planCode: e.target.value })); }}
                onBlur={handleCodeBlur}
                placeholder="e.g., MAP"
                maxLength={10}
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : undefined}
                autoFocus={mode === 'create' || mode === 'copy'}
              />
              {getFieldError('planCode') && <div className="form-error-message">{getFieldError('planCode')}</div>}
            </div>

            <div className="form-group" data-field="planName">
              <label className="form-label">Plan Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('planName') ? ' form-field-error' : ''}`}
                value={formData.planName}
                onChange={(e) => { clearFieldError('planName'); setFormData(prev => ({ ...prev, planName: e.target.value })); }}
                onBlur={handleNameBlur}
                placeholder="e.g., Modified American Plan"
                maxLength={50}
              />
              {getFieldError('planName') && <div className="form-error-message">{getFieldError('planName')}</div>}
            </div>
          </div>

          {/* --- Property & Status --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="propertyId">
              <SyutSelect
                label="Property"
                required
                options={propertyOptions}
                value={formData.propertyId}
                onChange={(val) => { clearFieldError('propertyId'); setFormData(prev => ({ ...prev, propertyId: (val as string) || '' })); }}
                placeholder="Select property..."
                isClearable
                size="md"
                error={getFieldError('propertyId')}
              />
            </div>

            <div className="form-group" data-field="isActive">
              <SyutSelect
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                value={String(formData.isActive)}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: val === 'true' }))}
                isClearable={false}
                size="md"
              />
            </div>
          </div>

          {/* --- Description --- */}
          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className={`form-input${getFieldError('description') ? ' form-field-error' : ''}`}
              value={formData.description}
              onChange={(e) => { clearFieldError('description'); setFormData(prev => ({ ...prev, description: e.target.value })); }}
              placeholder="Describe the meal plan..."
              maxLength={500}
              rows={3}
              style={{ resize: 'vertical' }}
            />
            {getFieldError('description') && <div className="form-error-message">{getFieldError('description')}</div>}
          </div>

          {/*
            Meals: Breakfast, then Main Meal — lunch and dinner as one choice, so
            MAP (breakfast + ONE of lunch or dinner) is a choice of its own instead
            of a field that only appears once both meals are ticked. Saved as
            includes_lunch / includes_dinner / default_major_meal
            (utils/mealPlanMeals.ts). Beverages is not on the form: nothing reads
            it, and the stored value is sent back unchanged.
          */}
          <div className="form-grid-3">
            <div className="form-group" data-field="includesBreakfast">
              <SyutSelect
                label="Breakfast"
                required
                value={String(formData.includesBreakfast)}
                onChange={(val) => setFormData(prev => ({ ...prev, includesBreakfast: val === 'true' }))}
                options={[
                  { value: 'true', label: 'Included' },
                  { value: 'false', label: 'Not included' },
                ]}
                isClearable={false}
                size="md"
              />
            </div>
            <div className="form-group" data-field="mainMeal">
              <SyutSelect
                label="Main Meal"
                required
                value={formData.mainMeal}
                onChange={(val) => {
                  clearFieldError('defaultMajorMeal');
                  setFormData(prev => ({ ...prev, mainMeal: ((val as string) || 'NONE') as MainMeal }));
                }}
                options={MAIN_MEAL_OPTIONS}
                isClearable={false}
                size="md"
              />
            </div>
            {formData.mainMeal === 'ONE_OF' ? (
              <div className="form-group" data-field="defaultMajorMeal">
                <SyutSelect
                  label="Default Main Meal"
                  required
                  value={formData.defaultMajorMeal}
                  onChange={(val) => {
                    clearFieldError('defaultMajorMeal');
                    setFormData(prev => ({ ...prev, defaultMajorMeal: (val as string) || '' }));
                  }}
                  options={[
                    { value: 'DINNER', label: 'Dinner' },
                    { value: 'LUNCH', label: 'Lunch' },
                  ]}
                  placeholder="Select the meal most guests take..."
                  isClearable={false}
                  size="md"
                  error={getFieldError('defaultMajorMeal')}
                />
              </div>
            ) : (
              <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {formData.mainMeal === 'BOTH'
                    ? 'Guests may take both lunch and dinner every day.'
                    : formData.mainMeal === 'NONE'
                      ? 'No lunch or dinner is included.'
                      : `Only ${formData.mainMeal === 'LUNCH' ? 'lunch' : 'dinner'} is included every day.`}
                </span>
              </div>
            )}
          </div>

          {formData.mainMeal === 'ONE_OF' && (
            <div className="form-grid-2">
              {formData.defaultMajorMeal ? (
                <>
                  <div className="form-group" data-field="majorMealRule">
                    <SyutSelect
                      label="Which Meal Counts"
                      value={formData.majorMealRule}
                      onChange={(val) => setFormData(prev => ({ ...prev, majorMealRule: ((val as string) || 'GUEST_CHOICE') as MajorMealRule }))}
                      options={[
                        { value: 'GUEST_CHOICE', label: "Guest's choice — lunch or dinner" },
                        { value: 'FIXED', label: `Only ${formData.defaultMajorMeal === 'LUNCH' ? 'lunch' : 'dinner'}, unless Front Office changes the day` },
                      ]}
                      isClearable={false}
                      size="sm"
                    />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      {formData.majorMealRule === 'FIXED'
                        ? `The restaurant counts only ${formData.defaultMajorMeal === 'LUNCH' ? 'lunch' : 'dinner'} as the plan meal. A guest who takes the other meal is billed, unless Front Office sets that day's meal on the stay.`
                        : `Whichever of lunch or dinner the guest takes first counts as the day's plan meal. The kitchen forecast counts these guests under ${formData.defaultMajorMeal === 'LUNCH' ? 'lunch' : 'dinner'}; check-in can record which meal the guest expects.`}
                    </span>
                  </div>
                </>
              ) : (
                <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Guests take breakfast and one of lunch or dinner each day. The default meal is used for the kitchen forecast and to decide whom the restaurant follows up.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* --- Rates --- */}
          <div className="form-grid-3">
            <div className="form-group" data-field="adultRate">
              <label className="form-label">Default Adult Rate</label>
              <input
                type="number"
                className={`form-input${getFieldError('adultRate') ? ' form-field-error' : ''}`}
                value={formData.adultRate}
                onChange={(e) => { clearFieldError('adultRate'); setFormData(prev => ({ ...prev, adultRate: e.target.value })); }}
                placeholder="e.g., 1500.00"
                min="0"
                step="0.01"
              />
              {getFieldError('adultRate') && <div className="form-error-message">{getFieldError('adultRate')}</div>}
            </div>

            <div className="form-group" data-field="childRate">
              <label className="form-label">Default Child Rate</label>
              <input
                type="number"
                className={`form-input${getFieldError('childRate') ? ' form-field-error' : ''}`}
                value={formData.childRate}
                onChange={(e) => { clearFieldError('childRate'); setFormData(prev => ({ ...prev, childRate: e.target.value })); }}
                placeholder="e.g., 750.00"
                min="0"
                step="0.01"
              />
              {getFieldError('childRate') && <div className="form-error-message">{getFieldError('childRate')}</div>}
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', minHeight: '2.25rem' }}>
                <input
                  type="checkbox"
                  checked={formData.includeTaxes}
                  onChange={(e) => setFormData(prev => ({ ...prev, includeTaxes: e.target.checked }))}
                  style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Tax Inclusive</span>
              </label>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Meal Plan'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the meal plan "${formData.planName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default MealPlanModal;
